import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { Toaster as Sonner } from 'sonner';
const FALLBACK_MOBILE_OFFSET = 'calc(16px + env(safe-area-inset-bottom, 0px))';
/**
 * Mobile bottom offset tracks the floating nav: when it hides via translateY,
 * toast drops closer to the bottom; when it shows, toast sits above it again.
 * Safari toast width is patched in apps/web `index.css` (do not relocate toasts to top).
 */
const Toaster = ({ resolvedTheme, ...props }) => {
    const [mobileBottomOffset, setMobileBottomOffset] = React.useState(FALLBACK_MOBILE_OFFSET);
    React.useEffect(() => {
        let frame = 0;
        const updateOffset = () => {
            if (window.innerWidth >= 1024) {
                setMobileBottomOffset(FALLBACK_MOBILE_OFFSET);
                return;
            }
            const mobileNav = document.querySelector('nav[aria-label="Main mobile navigation"]');
            if (!mobileNav) {
                setMobileBottomOffset(FALLBACK_MOBILE_OFFSET);
                return;
            }
            const navRect = mobileNav.getBoundingClientRect();
            const visibleHeight = Math.min(window.innerHeight, navRect.bottom) - Math.max(0, navRect.top);
            if (visibleHeight <= 0) {
                setMobileBottomOffset(FALLBACK_MOBILE_OFFSET);
                return;
            }
            // Keep toast above the currently visible nav slice + a small gap.
            const nextOffset = Math.ceil(visibleHeight + 16);
            setMobileBottomOffset(`calc(${nextOffset}px + env(safe-area-inset-bottom, 0px))`);
        };
        const scheduleUpdate = () => {
            if (frame) {
                cancelAnimationFrame(frame);
            }
            frame = requestAnimationFrame(updateOffset);
        };
        scheduleUpdate();
        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', scheduleUpdate, { passive: true });
        const mobileNav = document.querySelector('nav[aria-label="Main mobile navigation"]');
        // Nav hide/show is a style transform — scroll alone can miss the committed frame.
        const mutationObserver = mobileNav &&
            new MutationObserver(scheduleUpdate);
        if (mobileNav && mutationObserver) {
            mutationObserver.observe(mobileNav, { attributes: true, attributeFilter: ['style', 'class'] });
            mobileNav.addEventListener('transitionend', scheduleUpdate);
        }
        return () => {
            if (frame) {
                cancelAnimationFrame(frame);
            }
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', scheduleUpdate);
            mutationObserver?.disconnect();
            mobileNav?.removeEventListener('transitionend', scheduleUpdate);
        };
    }, []);
    return (_jsx(Sonner, { theme: resolvedTheme, className: "toaster group", mobileOffset: { bottom: mobileBottomOffset, left: '16px', right: '16px' }, toastOptions: {
            classNames: {
                toast: 'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
                description: 'group-[.toast]:text-muted-foreground',
                actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
                cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
            },
        }, ...props }));
};
export { Toaster };
